// @ts-nocheck
/**
 * ツインキーシステム - 結果画面
 * @module twinKey/ui/screens/ResultScreen
 */

import React from 'react';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useTwinKey } from '../../hooks';
import styles from '../styles/twinKeyStyles';
import {
  ATTRIBUTE_MATERIALS,
  CATEGORY_MATERIALS,
  GENERIC_MATERIALS,
  RARITY_FRAGMENTS,
  EXP_ORB_MATERIALS,
} from '../../content/alchemy/data/materialTypes';

/**
 * 素材IDまたは日本語名から素材情報を取得
 */
function getMaterialByIdOrName(materialIdOrName) {
  const allMaterials = {
    ...ATTRIBUTE_MATERIALS,
    ...CATEGORY_MATERIALS,
    ...GENERIC_MATERIALS,
    ...RARITY_FRAGMENTS,
    ...EXP_ORB_MATERIALS,
  };

  // 英語IDで検索
  if (allMaterials[materialIdOrName]) {
    return allMaterials[materialIdOrName];
  }

  // 日本語名で検索
  for (const mat of Object.values(allMaterials)) {
    if (mat.name === materialIdOrName) {
      return mat;
    }
  }

  // 見つからない場合はデフォルト（日本語名をそのまま表示）
  return { id: materialIdOrName, name: materialIdOrName, icon: '📦', tier: 'common' };
}

/**
 * 探索結果画面
 */
export function ResultScreen() {
  const { state, onDungeonComplete } = useTwinKeyContext();
  const { goToHub, saveExpeditionToHistory } = useTwinKey();

  // ハブに戻る時に統計を保存し、日付を進める
  const handleReturnToHub = () => {
    saveExpeditionToHistory();
    // ダンジョン完了時の日付進行処理（市場更新、価格履歴、大会処理など）
    if (onDungeonComplete) {
      onDungeonComplete();
    }
    goToHub();
  };

  const result = state.expeditionResult;

  if (!result) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>探索結果</h1>
        </div>
        <div style={styles.emptyMessage}>
          結果情報がありません
        </div>
        <button style={styles.primaryButton} onClick={handleReturnToHub}>
          ハブに戻る
        </button>
      </div>
    );
  }

  // isVictory/isDefeat/isRetreated は endDungeon 呼び出し時に設定される
  const isVictory = result.isVictory === true;
  const isEscaped = result.isRetreated === true;

  return (
    <div style={styles.resultScreenContainer}>
      {/* 背景エフェクト */}
      <div
        style={{
          ...styles.resultBackground,
          background: isVictory
            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(26, 26, 46, 1) 100%)'
            : isEscaped
            ? 'linear-gradient(135deg, rgba(255, 149, 0, 0.2) 0%, rgba(26, 26, 46, 1) 100%)'
            : 'linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(26, 26, 46, 1) 100%)',
        }}
      />

      {/* メインコンテンツ */}
      <div style={styles.resultMainContent}>
        {/* 結果タイトル */}
        <div style={styles.resultTitleSection}>
          <div
            style={{
              ...styles.resultTitleIcon,
              color: isVictory ? '#4CAF50' : isEscaped ? '#ff9500' : '#f44336',
            }}
          >
            {isVictory ? '🏆' : isEscaped ? '🚪' : '💀'}
          </div>
          <h1
            style={{
              ...styles.resultTitleText,
              color: isVictory ? '#4CAF50' : isEscaped ? '#ff9500' : '#f44336',
            }}
          >
            {isVictory ? '探索成功！' : isEscaped ? '撤退完了' : '探索失敗...'}
          </h1>
        </div>

        {/* ダンジョン情報 */}
        <div style={styles.resultDungeonInfo}>
          <div style={styles.dungeonResultName}>{result.dungeonName}</div>
          <div style={styles.dungeonResultDetails}>
            到達階層: {result.floorReached}F / {result.totalFloors}F
          </div>
        </div>

        {/* 統計 */}
        <div style={styles.resultStatsSection}>
          <h2 style={styles.resultSectionTitle}>📊 探索統計</h2>
          <div style={styles.resultStatsGrid}>
            <StatCard
              icon="⚔️"
              label="戦闘回数"
              value={result.stats?.battlesWon || 0}
              subLabel={`/ ${result.stats?.battlesTotal || 0}`}
            />
            <StatCard
              icon="💥"
              label="総ダメージ"
              value={formatNumber(result.stats?.totalDamageDealt || 0)}
            />
            <StatCard
              icon="🎯"
              label="クリティカル"
              value={result.stats?.totalCriticals || 0}
            />
            <StatCard
              icon="🔥"
              label="スキル使用"
              value={result.stats?.totalSkillsUsed || 0}
            />
          </div>
        </div>

        {/* 報酬 */}
        <div style={styles.resultRewardsSection}>
          <h2 style={styles.resultSectionTitle}>💎 獲得報酬</h2>
          <div style={styles.rewardsGrid}>
            {/* ゴールド */}
            <div style={styles.rewardItem}>
              <div style={styles.rewardIcon}>💰</div>
              <div style={styles.rewardInfo}>
                <div style={styles.rewardLabel}>探索G</div>
                <div style={styles.rewardValue}>
                  +{formatNumber((result.rewards?.gold || 0) + (result.collectedGold || 0))} G
                </div>
              </div>
            </div>

            {/* 経験値 */}
            <div style={styles.rewardItem}>
              <div style={styles.rewardIcon}>✨</div>
              <div style={styles.rewardInfo}>
                <div style={styles.rewardLabel}>経験値</div>
                <div style={styles.rewardValue}>
                  +{formatNumber(result.rewards?.exp || 0)} EXP
                </div>
              </div>
            </div>

            {/* アイテム */}
            {result.rewards?.items?.length > 0 && (
              <div style={styles.rewardItemsSection}>
                <div style={styles.rewardItemsTitle}>獲得アイテム</div>
                <div style={styles.rewardItemsList}>
                  {result.rewards.items.map((item, index) => (
                    <div key={index} style={styles.rewardItemEntry}>
                      <span style={styles.itemIcon}>{item.icon || '📦'}</span>
                      <span style={styles.itemName}>{item.name}</span>
                      <span style={styles.itemCount}>x{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 収集素材 */}
        {result.collectedMaterials && Object.keys(result.collectedMaterials).length > 0 && (
          <div style={styles.resultMaterialsSection}>
            <h2 style={styles.resultSectionTitle}>⚗️ 収集素材</h2>
            <div style={styles.materialsGrid}>
              {Object.entries(result.collectedMaterials).map(([materialId, count]) => {
                const material = getMaterialByIdOrName(materialId);
                return (
                  <MaterialItem key={materialId} material={material} count={count} />
                );
              })}
            </div>
          </div>
        )}

        {/* パーティ状態 */}
        <div style={styles.resultPartySection}>
          <h2 style={styles.resultSectionTitle}>👥 パーティ状態</h2>
          <div style={styles.partyResultGrid}>
            {result.partyState?.map((member, index) => (
              <PartyMemberResult key={member.uniqueId || index} member={member} />
            ))}
          </div>
        </div>

        {/* アクションボタン */}
        <div style={styles.resultActions}>
          <button style={styles.primaryButton} onClick={handleReturnToHub}>
            ハブに戻る
          </button>
          {isVictory && (
            <button style={styles.secondaryButton} onClick={handleReturnToHub}>
              もう一度挑戦
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 統計カード
 */
function StatCard({ icon, label, value, subLabel }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statCardIcon}>{icon}</div>
      <div style={styles.statCardInfo}>
        <div style={styles.statCardLabel}>{label}</div>
        <div style={styles.statCardValue}>
          {value}
          {subLabel && <span style={styles.statCardSubLabel}>{subLabel}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * パーティメンバー結果
 */
function PartyMemberResult({ member }) {
  const attrColor = styles.getAttributeColor(member.attribute);
  const hpPercent = member.maxHp
    ? (member.currentHp / member.maxHp) * 100
    : 0;
  const hpColor = styles.getHpColor(hpPercent);
  const isAlive = member.currentHp > 0;

  return (
    <div
      style={{
        ...styles.partyMemberResult,
        borderColor: attrColor,
        opacity: isAlive ? 1 : 0.5,
      }}
    >
      <div
        style={{
          ...styles.memberResultIcon,
          background: attrColor,
        }}
      >
        {styles.getAttributeIcon(member.attribute)}
      </div>
      <div style={styles.memberResultInfo}>
        <div style={styles.memberResultName}>{member.name}</div>
        <div style={styles.memberResultLevel}>Lv.{member.level}</div>
        <div style={styles.memberResultHpBar}>
          <div
            style={{
              ...styles.memberResultHpFill,
              width: `${hpPercent}%`,
              background: hpColor,
            }}
          />
        </div>
        <div style={styles.memberResultHpText}>
          {member.currentHp} / {member.maxHp}
        </div>
      </div>
      {!isAlive && (
        <div style={styles.memberDeadBadge}>💀</div>
      )}
      {member.levelUp && (
        <div style={styles.levelUpBadge}>LEVEL UP!</div>
      )}
    </div>
  );
}

/**
 * 素材アイテム表示
 */
function MaterialItem({ material, count }) {
  const tierColors = {
    common: '#9e9e9e',
    uncommon: '#4CAF50',
    rare: '#2196F3',
    epic: '#9C27B0',
    legendary: '#FF9800',
  };

  const tierColor = tierColors[material.tier] || tierColors.common;

  return (
    <div style={styles.materialItem}>
      <div style={{
        ...styles.materialIcon,
        borderColor: tierColor,
        boxShadow: `0 0 8px ${tierColor}40`,
      }}>
        {material.icon}
      </div>
      <div style={styles.materialInfo}>
        <div style={{
          ...styles.materialName,
          color: tierColor,
        }}>
          {material.name}
        </div>
        <div style={styles.materialCount}>x{count}</div>
      </div>
    </div>
  );
}

/**
 * 数値フォーマット
 */
function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export default ResultScreen;
